import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";

export default function MessagesPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const scrollRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/threads");
    setThreads(data);
    if (data.length && !active) setActive(data[0]);
  };

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/threads/${active.id}/messages`).then(({ data }) => {
      setMessages(data.messages);
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    });
  }, [active?.id]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active?.other) return;
    const { data } = await api.post("/messages", {
      recipient_id: active.other.id,
      thread_id: active.id,
      content: body,
    });
    setMessages((prev) => [...prev, data.message]);
    setBody("");
    setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
    loadThreads();
  };

  return (
    <div className="pt-24 sm:pt-28 min-h-screen max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
      <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Messagerie</div>
      <h1 className="font-display text-4xl font-bold tracking-tight mb-8">Conversations</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 border border-black/10 bg-white h-[600px]">
        {/* Threads list */}
        <div className="md:col-span-1 border-r border-black/10 overflow-y-auto">
          {threads.length === 0 && (
            <div className="p-6 text-sm text-neutral-500 italic">Aucune conversation pour le moment.</div>
          )}
          {threads.map((t) => (
            <button
              key={t.id}
              data-testid={`thread-${t.id}`}
              onClick={() => setActive(t)}
              className={`w-full text-left p-4 border-b border-black/10 hover:bg-neutral-50 ${active?.id === t.id ? "bg-neutral-100" : ""}`}
            >
              <div className="font-semibold">{t.other?.name || "—"}</div>
              <div className="text-xs text-neutral-500 mt-1 truncate">{t.last_message}</div>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className="md:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-neutral-400">Sélectionnez une conversation</div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-black/10">
                <div className="font-semibold">{active.other?.name}</div>
                <div className="text-xs text-neutral-500">{active.other?.email}</div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3 bg-[#FAFAFA]">
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] p-3 ${mine ? "bg-[#0A0A0A] text-white" : "bg-white border border-black/10"}`}>
                        <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-neutral-400"}`}>
                          {new Date(m.created_at).toLocaleString("fr-FR")}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="border-t border-black/10 p-4 flex gap-2">
                <input
                  data-testid="msg-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Votre message…"
                  className="flex-1 border border-black/15 px-3 py-2 outline-none focus:border-[#0A0A0A]"
                />
                <button data-testid="msg-send" type="submit" className="bg-[#0A0A0A] text-white px-5 font-semibold hover:bg-[#1B2A49] transition-colors flex items-center gap-2">
                  <Send className="w-4 h-4" /> Envoyer
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
